<script>
import 'jsplumb';
import 'jsplumb/css/jsplumbtoolkit-defaults.css';
import { Confirm } from '../../services/Confirm';

/**
 * Zooms list.
 */
const zooms = [
  1,
  0.85,
  0.7,
  0.55,
  0.4,
  0.25,
];

/**
 * Format seconds to string.
 */
const humanize = (seconds) => {
  if (seconds == 0) {
    return 'Imediatamente';
  }

  if (seconds >= 86400 && (seconds % 86400) == 0) {
    const days = seconds / 86400;

    return `Executar após ${days} ${days > 1 ? 'dias' : 'dia'}`;
  }

  if (seconds >= 3600 && (seconds % 3600) == 0) {
    const hours = seconds / 3600;

    return `Executar após ${hours} ${hours > 1 ? 'horas' : 'hora'}`;
  }

  const minutes = seconds / 60;

  return `Executar após ${minutes} ${minutes > 1 ? 'minutos' : 'minuto'}`;
};

/**
 * Recursive connect stuff.
 */
const connect = (plumb, actions) => {
  actions.map((action, index) => {
    const next = actions[index + 1];
    const hasChildren = Array.isArray(action.children) && action.children.length > 0;

    if (next) {
      /**
       * Connect an action card to the next.
       */
      plumb.connect({
        source: action.id,
        target: next.id,
        connector: ['Straight', {
          gap: 12,
        }],
        anchors: ['Bottom', 'Top'],
        endpoint: 'Blank',
        overlays: [
          ['Custom', {
            create (component) {
              return next.delayId;
            },

            location: 0.5,
          }],
        ],
      });
    } else {
      /**
       * Connect an action card to "add new action" button.
       */
      plumb.connect({
        source: action.id,
        target: action.addActionId,
        connector: ['Straight', {
          gap: 12,
        }],
        anchors: ['Bottom', 'Top'],
        endpoint: 'Blank',
      });
    }

    if (! action.hasDecisions) {
      return;
    }

    if (hasChildren) {
      /**
       * Connect an action card to his first decision
       * and use the "add new decision" button as overlay.
       */
      plumb.connect({
        source: action.id,
        target: action.children[0].id,
        connector: ['Straight', {
          gap: 12,
        }],
        anchors: ['Right', 'Left'],
        endpoint: 'Blank',
        overlays: [
          ['Custom', {
            create (component) {
              return action.addDecisionId;
            },

            location: 0.5,
          }],
        ],
      });

      /**
       * Iterate through action decisions.
       */
      action.children.map(({
        decision, id, noId, yesId, items,
      }, index) => {
        const {
          id: target,
        } = action.children[index + 1] || {};

        if (target) {
          /**
           * Connect an decision card to the next decision.
           */
          plumb.connect({
            target,
            source: id,
            connector: ['Straight', {
              gap: 12,
            }],
            anchors: ['Right', 'Left'],
            endpoint: 'Blank',
          });
        }

        /**
         * If we have actions for option "yes".
         */
        if (items['1'] && items['1'].length) {
          /**
           * Connect a decision card to his first action
           * and use the "yes" button as overlay.
           */
          plumb.connect({
            source: id,
            target: items['1'][0].id,
            connector: ['Bezier', { curviness: 105 }],
            anchors: ['Bottom', 'Top'],
            endpoint: 'Blank',
            overlays: [
              ['Custom', {
                create (component) {
                  return yesId;
                },

                location: 0.5,
              }],

              ['Custom', {
                create (component) {
                  return items['1'][0].delayId;
                },

                location: 0.96,
              }],
            ],
          });
        } else {
          /**
           * Connect an decision card to his "yes" button.
           */
          plumb.connect({
            source: id,
            target: yesId,
            connector: ['Bezier', { curviness: 70 }],
            anchors: ['Bottom', 'Right'],
            endpoint: 'Blank',
          });
        }

        /**
         * If we have actions for option "no".
         */
        if (items['0'] && items['0'].length) {
          /**
           * Connect a decision card to his first action
           * and use the "no" button as overlay.
           */
          plumb.connect({
            source: id,
            target: items['0'][0].id,
            connector: ['Bezier', { curviness: 105 }],
            anchors: ['Bottom', 'Top'],
            endpoint: 'Blank',
            overlays: [
              ['Custom', {
                create (component) {
                  return noId;
                },

                location: 0.5,
              }],

              ['Custom', {
                create (component) {
                  return items['0'][0].delayId;
                },

                location: 0.96,
              }],
            ],
          });
        } else {
          /**
           * Connect a decision card to his "no" button.
           */
          plumb.connect({
            source: id,
            target: noId,
            connector: ['Bezier', { curviness: 70 }],
            anchors: ['Bottom', 'Left'],
            endpoint: 'Blank',
          });
        }

        Object.keys(items)
          .map((key) => items[key] && connect(plumb, items[key]));
      });
    } else {
      /**
       * Connect an action card to his "add new decision" button.
       */
      plumb.connect({
        source: action.id,
        target: action.addDecisionId,
        connector: ['Straight', {
          gap: 12,
        }],
        anchors: ['Right', 'Left'],
        endpoint: 'Blank',
      });
    }
  });
};

/**
 * Render JSPlumb.
 */
const render = (plumb, actions, rules, auto = true) => {
  if (actions.length > 0) {
    const {
      id,
      delayId,
    } = actions[0];

    /**
     * Connect action step to first action card.
     * Use delay as overlay.
     */
    plumb.connect({
      source: 'step.2.help',
      target: id,
      connector: ['Straight', {
        gap: 12,
      }],
      anchors: ['Bottom', 'Top'],
      endpoint: 'Blank',
      overlays: [
        ['Custom', {
          create (component) {
            return delayId;
          },

          location: 0.5,
        }],
      ],
    });
  } else {
    plumb.connect({
      source: 'step.2.help',
      target: 'add-action',
      connector: ['Straight', {
        gap: 12,
      }],
      anchors: ['Bottom', 'Top'],
      endpoint: 'Blank',
    });
  }

  if (! auto) {
    return connect(plumb, actions);
  }

  /**
   * If we don't have rules.
   */
  if (rules.length == 0) {
    const connection = actions.length == 0
      ? {
        target: 'add-rule',
      }
      : {
        target: 'step.2.title',
        overlays: [
          ['Custom', {
            create (component) {
              return 'add-rule';
            },

            location: 0.5,
          }],
        ],
      };

    plumb.connect({
      source: 'step.1.help',
      connector: ['Straight', {
        gap: 12,
      }],
      anchors: ['Bottom', 'Top'],
      endpoint: 'Blank',
      ...connection,
    });
  } else {
    /**
     * Connect rule step to first rule card.
     * Use add rule button as overlay.
     */
    plumb.connect({
      source: 'step.1.help',
      target: `rule.${rules[0].id}`,
      connector: ['Straight', {
        gap: 12,
      }],
      anchors: ['Bottom', 'Top'],
      endpoint: 'Blank',
      overlays: [
        ['Custom', {
          create (component) {
            return 'add-rule';
          },

          location: 0.5,
        }],
      ],
    });

    /**
     * Connect last rule card to action step.
     */
    plumb.connect({
      source: `rule.${rules[rules.length - 1].id}`,
      target: 'step.2.title',
      connector: ['Straight', {
        gap: 12,
      }],
      anchors: ['Bottom', 'Top'],
      endpoint: 'Blank',
    });
  }

  connect(plumb, actions);
};

/**
 * Truncate a string.
 */
const truncate = (string = '', length = 70) => {
  if (string && string.length > length) {
    return `${string.substring(0, length).trim()}...`;
  }

  return string;
};

/**
 * Set opacity.
 */
const setOpacity = (element, opacity) => element.style.opacity = opacity;

/**
 * Delay.
 */
const delay = (callback, timeout = 1000) => setTimeout(callback, 1000);

/**
 * Recursive sum offsetTop from offsetParent.
 */
const sumOffsetTop = (element) => (element ? (element.offsetTop + (element.offsetParent ? sumOffsetTop(element.offsetParent) : 0)) : 0);

/**
 * Recursive format actions to a tree.
 */
const format = (actions, jobs, parent = null) => actions.reduce((tree, action) => {
  /**
   * Just in case.
   */
  if (! (action && action.job)) {
    return tree;
  }

  if (action.parent_id == parent) {
    /**
     * Build children array grouped by decision
     */
    const children = format(actions, jobs, action.id)
      .reduce((children, child) => {
        const {
          decision: {
            option,
            decision,
          },
        } = child;

        /**
         * Just in case²
         */
        if (decision) {
          if (! children[decision]) {
            children[decision] = {
              //
            };
          }

          if (! children[decision][option]) {
            children[decision][option] = [];
          }

          children[decision][option].push(child);
        }

        return children;
      }, {
        //
      });

    const parsed = action.id.toString();

    const {
      decisions,
    } = jobs[action.type] || {};

    tree.push({
      ...action,
      id: parsed,
      delayId: `${parsed}.delay`,
      addActionId: `${parsed}.add-action`,
      addDecisionId: `${parsed}.add-decision`,
      hasDecisions: decisions
        ? Object.keys(decisions).length > 0
        : false,
      description: truncate(action.actionable ? (action.actionable.title || action.actionable.name) : action.value),
      delayHumanized: humanize(action.delay),
      children: Object.keys(children)
        .map((key) => {
          const [
            job,
            event,
          ] = key.split('-');

          return {
            id: `${parsed}.${key}`,
            noId: `${parsed}.${key}.no`,
            yesId: `${parsed}.${key}.yes`,
            decision: {
              key: event,
              path: key,
              name: jobs[job].decisions[event].name,
            },
            items: children[key],
          };
        }),
    });
  }

  return tree;
}, [
  //
]);

/**
 * Recursive find an action in tree.
 */
const findInTree = (id, branch) => {
  for (const node of branch) {
    if (node.id == id) {
      return node;
    }

    if (! (Array.isArray(node.children) && node.children.length > 0)) {
      continue;
    }

    for (const child of node.children) {
      const values = Object.values(child.items).reduce((a, b) => a.concat(b), []);
      const action = findInTree(id, values);

      if (action) {
        return action;
      }
    }
  }

  return null;
};

export default {
  props: {
    user: {
      type: Object,
      required: true,
    },

    id: {
      type: Number,
      required: true,
    },

    type: {
      type: String,
      required: true,
    },

    auto: {
      type: Boolean,
      required: false,
      default: false,
    },

    jobs: {
      type: Object,
      required: true,
    },

    available: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    zoomIndex: Number(window.localStorage.getItem('zoomAutomation')) || 1,
    counter: 0,
    maxWidth: 0,
    maxHeight: 0,
    height: 0,
    margin: {
      x: 0,
      y: 0,
      top: 0,
      left: 0,
      prevTop: 0,
      prevLeft: 0,
    },
    active: null,
    action: {},
    drag: false,
    rules: [],
    saving: false,
    isEmpty: false,
    tree: [],
    actions: null,
    show: null,
    elements: {
      rule: null,
      decision: null,
    },
  }),

  watch: {
    active (active, old) {
      if (active == old) {
        return;
      }

      const element = document.getElementById(active);
      const oldElement = document.getElementById(old);

      if (element) {
        setOpacity(element, 0.5);
      }

      if (oldElement) {
        setOpacity(oldElement, 1);
      }
    },

    actions: {
      handler (actions) {
        this.tree = Array.isArray(actions) && actions.length > 0
          ? format(actions, this.jobs)
          : [];
      },
      immediate: true,
    },

    show (flag) {
      if (flag) {
        return;
      }

      this.active = null;
    },
  },

  mounted () {
    this.loadActions();
    this.loadRules();

    const updateHeight = () => {
      setTimeout(() => {
        this.updateMaxSizes();

        this.height = window.innerHeight - sumOffsetTop(this.$refs.container);
      }, 1500);
    };

    updateHeight();

    Bus.$on('bulldeskHashChanged', (hash) => {
      updateHeight();

      if (hash != 'actions') {
        return;
      }

      delay(() => this.initPlumb());
    });

    window.addEventListener('resize', updateHeight, true);

    this.$once('hook:destroyed', () => {
      window.removeEventListener('resize', updateHeight, true);
    });
  },

  computed: {
    style () {
      return {
        height: this.height ? `${this.height}px` : '100vh',
      };
    },

    types () {
      return [
        {
          value: 'all',
          label: 'Todas',
          description: 'Todas as regras devem ser respeitadas',
        },
        {
          value: 'any',
          label: 'Qualquer',
          description: 'Apenas uma das regras precisa ser respeitada',
        },
      ];
    },

    zoom () {
      return zooms[this.zoomIndex];
    },

    selectedAutomationTypeDescription () {
      const {
        type,
        types,
      } = this;

      const {
        description = '',
      } = types.find(({ value }) => value === type) || {};

      return description;
    },

    wrapper () {
      const {
        top = 0,
        left = 0,
      } = this.margin || {};

      return {
        marginTop: `${top}px`,
        marginLeft: `${left}px`,
        transform: `scale(${this.zoom}) translateY(${(200 * (1 - this.zoom))}px)`,
      };
    },

    addAction () {
      return {
        actions: this.jobs,
        value: this.action,
        disabled: this.saving,
      };
    },

    addDecision () {
      const {
        type,
      } = this.action || {};

      const {
        decisions = [],
      } = this.jobs[type] || {};

      return {
        decisions: Object.keys(decisions)
          .map((key) => ({
            key,
            ...decisions[key],
          })),
      };
    },

    addRule () {
      return {
        id: this.id,
        rules: this.available,
        automation_rules: this.rules,
      };
    },
  },

  provide () {
    return {
      builder: this,
      addAction: this.handleAddAction,
      addDecision: this.handleAddDecision,
      removeAction: this.handleRemoveAction,
      removeDecision: this.handleRemoveDecision,
    };
  },

  methods: {
    syncRules () {
      this.$emit('rules', this.rules);
    },

    initPlumb () {
      this.counter += 1;

      [...document.querySelectorAll('.jtk-overlay')].forEach((node) => node.remove());

      this.$nextTick(() => {
        this.$forceUpdate();

        const {
          wrapper: Container,
        } = this.$refs;

        const instance = jsPlumb.getInstance({
          Container,
          PaintStyle: {
            strokeWidth: 2, stroke: '#b2a8c1', dashstyle: '1 2',
          },
        });

        if (this.instance) {
          const connections = this.instance.getAllConnections();

          instance.batch(() => {
            connections.map((a) => a.connector.cleanup());
          });

          this.instance = null;
        }

        instance.ready(() => {
          instance.batch(() => {
            render(instance, this.actions ? this.tree : [], this.rules, this.auto);

            this.$nextTick(() => {
              this.updateMaxSizes();
            });
          });

          this.instance = instance;
        });
      });
    },

    async loadActions () {
      try {
        const {
          data,
        } = await this.$http.get(`/automation/${this.id}/action`);

        this.actions = data;

        this.initPlumb();
      } catch (e) {
        this.actions = false;
      }
    },

    async loadRules () {
      try {
        const {
          data,
        } = await this.$http.get(`/automation/${this.id}/rule`);

        this.rules = data;

        this.initPlumb();
        this.syncRules();
      } catch (e) {
        this.rules = [];
      }
    },

    async saveAction (data) {
      this.saving = true;

      const {
        id = null,
        decision = null,
        parent_id = null,
      } = this.action || {};

      const formated = {
        ...data,
        decision,
        parent_id,
      };

      try {
        await (
          id
            ? this.$http.put(`/automation/${this.id}/action/${id}`, formated)
            : this.$http.post(`/automation/${this.id}/action`, formated)
        );

        this.loadActions();
      } catch (e) {
        if (e.status === 403) {
          Alert.error(e.data.error);
        }
        if(e.status === 400) {
          Alert.error(e.body.error)
        }
      }

      this.show = null;
      this.action = {};
      this.saving = false;
    },

    async saveDecision ({ key, path, name }) {
      this.show = null;

      const {
        id,
      } = this.action || {};

      if (! id) {
        return;
      }

      const action = findInTree(id, this.tree);

      if (! action) {
        return;
      }

      action.children.push({
        id: `${id}.${key}`,
        noId: `${id}.${key}.no`,
        yesId: `${id}.${key}.yes`,
        decision: {
          key,
          name,
          path: `${action.type}-${key}`,
        },
        items: [],
      });

      this.initPlumb();
    },

    async saveRule (data) {
      this.saving = true;

      try {
        await this.$http.post(`/automation/${this.id}/rule`, data);

        this.loadRules();
      } catch (e) {
        //
      }

      this.show = null;
      this.saving = false;
    },

    async handleRemoveAction (id) {
      if (! await Confirm(this.__('Deseja remover esta ação?'))) {
        return;
      }

      try {
        await this.removeAction(id);
      } catch (e) {
        Alert.error(e.message);
      }

      this.initPlumb();
    },

    async removeAction (id) {
      this.$http.delete(`/automation/${this.id}/action/${id}`);

      this.actions.splice(this.actions.findIndex((action) => action.id == id), 1);
    },

    async handleRemoveDecision (id, parent, actions = []) {
      if (! await Confirm(this.__('Deseja excluir a decisão?'))) {
        return;
      }

      const filtered = actions.filter((id) => id);

      try {
        if (filtered.length) {
          return await Promise.all(filtered.map((id) => this.removeAction(id)));
        }

        const action = findInTree(parent, this.tree);

        if (! action) {
          return;
        }

        this.$delete(action.children, action.children.findIndex((decision) => decision.id == id));
      } catch (e) {
        Alert.error(e.message);
      } finally {
        this.initPlumb();
      }
    },

    async handleRemoveRule (id) {
      if (! await Confirm(this.__('Deseja excluir a regra?'))) {
        return;
      }

      try {
        await this.removeRule(id);
      } catch (e) {
        Alert.error(e.message);
      }

      this.initPlumb();
    },

    async removeRule (id) {
      await this.$http.delete(`/automation/${this.id}/rule/${id}`);

      this.rules.splice(this.rules.findIndex((rule) => rule.id == id), 1);

      this.syncRules();
    },

    handleMouseDown (event) {
      if (! this.instance) {
        return;
      }

      this.drag = true;

      const {
        top: prevTop,
        left: prevLeft,
      } = this.margin;

      this.margin = {
        prevTop,
        prevLeft,
        top: prevTop,
        left: prevLeft,
        y: event.pageY,
        x: event.pageX,
      };
    },

    handleMouseMove (event) {
      if (! this.drag) {
        return;
      }

      event.preventDefault();

      const {
        x,
        y,
        top,
        left,
        prevTop,
        prevLeft,
      } = this.margin;

      this.margin.top = Math.min(0, prevTop + (event.pageY - y));
      this.margin.left = Math.min(0, prevLeft + (event.pageX - x));
    },

    handleZoom (dir) {
      if (! this.instance) {
        return;
      }

      /**
       * @todo Shift margins to center chart when zooming.
       */
      this.zoomIndex = Math.min(zooms.length - 1, Math.max(0, this.zoomIndex + dir));

      window.localStorage.setItem('zoomAutomation', this.zoomIndex);
    },

    handleMouseWheel (event) {
      this.margin.top = Math.max(this.maxHeight * -1 * this.zoom, Math.min(0, this.margin.top + event.wheelDeltaY));
      this.margin.left = Math.max(this.maxWidth * -1 * this.zoom, Math.min(0, this.margin.left + event.wheelDeltaX));
    },

    updateMaxSizes () {
      this.maxHeight = Math.max(
        ...(
          [
            ...document.querySelectorAll('.automation-builder-tree__add-action'),
          ]
            .map((element) => element.offsetTop)
        ),
      );
      this.maxWidth = Math.max(
        ...(
          [
            ...document.querySelectorAll('.automation-builder-tree__add-decision'),
          ]
            .map((element) => element.offsetLeft)
        ),
      );
    },

    handleAddRule (id) {
      this.active = id;
      this.show = 'rule';
    },

    handleAddDecision (action, id) {
      this.active = id;
      this.action = action;
      this.show = 'decision';
    },

    handleAddAction (action, id, empty = false) {
      this.active = id;
      this.action = action;
      this.isEmpty = empty;
      this.show = 'action';
    },

    toggleAutomationType () {
      const map = {
        all: 'any',
        any: 'all',
      };

      this.$emit('update:type', map[this.type]);
    },
  },
};
</script>

<template>
  <section
    :key="counter"
    ref="container"
    @mouseup="drag = null"
    @mouseleave="drag = null"
    class="automation-builder"
    @mousemove="handleMouseMove"
    @mousewheel="handleMouseWheel"
    @mousedown.prevent="handleMouseDown"
  >
    <div class="automation-builder__header">
      <button
        @click.prevent="handleZoom(1)"
        class="automation-builder__zoom"
      >
        <i class="fa fa-search-minus" />
      </button>

      <button
        @click.prevent="handleZoom(-1)"
        class="automation-builder__zoom"
      >
        <i class="fa fa-search-plus" />
      </button>

      {{ Math.floor(zoom * 100) }}%
    </div>

    <Transition name="slide-right">
      <AutomationBuilderAddAction
        :key="active"
        v-bind="addAction"
        :is-empty="isEmpty"
        @save="saveAction"
        @close="show = null"
        v-if="show == 'action'"
      />
    </Transition>

    <Transition name="slide-right">
      <AutomationBuilderAddRule
        :key="active"
        v-bind="addRule"
        @save="saveRule"
        v-if="show == 'rule'"
        @close="show = null"
      />
    </Transition>

    <Transition name="slide-right">
      <AutomationBuilderAddDecision
        :key="active"
        v-bind="addDecision"
        @save="saveDecision"
        @close="show = null"
        v-if="show == 'decision'"
      />
    </Transition>

    <div
      ref="wrapper"
      id="wrapper"
      :style="wrapper"
      class="automation-builder__wrapper"
    >
      <template v-if="auto">
        <div class="automation-builder__step">
          <h4 class="automation-builder__step-title">
            {{ __('Etapa 1') }}
          </h4>

          <p class="automation-builder__step-description">
            {{ __('Regras da campanha') }}
          </p>

          <div class="automation-builder__step-type">
            <button
              class="automation-builder__toggle"
              @click.prevent="toggleAutomationType"
              :class="{ 'automation-builder__toggle--active': type === 'all' }"
            >
              <span
                class="automation-builder__btn automation-builder__btn--inline"
              />
            </button>

            <p
              class="automation-builder__step-help"
              v-text="__(selectedAutomationTypeDescription)"
            />
          </div>

          <p
            id="step.1.help"
            class="automation-builder__step-help"
          >
            {{ __('Clique no “+” abaixo para adicionar uma regra') }}
          </p>
        </div>

        <button
          id="add-rule"
          @click="handleAddRule('add-rule')"
          class="automation-builder__add-rule"
          :class="{ 'automation-builder__add-rule--not-empty': rules.length > 0 || actions && actions.length }"
        >
          +
        </button>

        <ul
          class="automation-builder__rules"
        >
          <li
            :key="rule.id"
            :id="`rule.${rule.id}`"
            v-for="rule in rules"
            class="automation-builder__rule"
          >
            <div class="d-flex align-items-center">
              <IconList />

              <span class="ml-2">
                {{ __(rule.name) }}
                <br>
                <span
                  class="automation-builder__rule-name"
                  v-if="rule.automatable && rule.automatable.name"
                >"{{ __(rule.automatable.name) }}"</span>
                <span
                  class="automation-builder__rule-name"
                  v-if="rule.automatable && rule.type == 'date' && rule.automatable.description"
                >"
                  <span>{{ rule.automatable.description }}: {{ rule.option }} dias antes"</span>
                </span>
                <span
                  class="automation-builder__rule-name"
                  v-if="! rule.automatable && rule.value"
                >"{{ __(rule.value) }}"</span>
              </span>

              <button
                class="automation-builder__rule-delete"
                @click.prevent="handleRemoveRule(rule.id)"
              >
                <IconTrash color="#F6F5F8" />
              </button>
            </div>
          </li>
        </ul>
      </template>

      <div class="automation-builder__step">
        <h4
          v-if="auto"
          id="step.2.title"
          class="automation-builder__step-title"
        >
          {{ __('Etapa 2') }}
        </h4>

        <p class="automation-builder__step-description">
          {{ __('Defina as ações da campanha') }}
        </p>

        <p
          id="step.2.help"
          class="automation-builder__step-help"
        >
          {{ __('Clique no “+” abaixo para adicionar uma ação') }}
        </p>
      </div>

      <AutomationBuilderTree
        :actions="tree"
      />
    </div>
  </section>
</template>

<style lang="scss">
  @import '../../../sass/variables';

  .automation-builder {
    height: 100%;
    cursor: grab;
    overflow: hidden;
    position: relative;
    min-height: 1000px;

    &__header {
      top: 16px;
      left: 16px;
      z-index: 99;
      width: 117px;
      display: flex;
      padding: 6px 10px;
      background: #FFF;
      position: absolute;
      border-radius: 4px;
      align-items: center;
      box-shadow: 0 5px 10px rgba(165, 131, 224, 0.1);
    }

    &__zoom {
      border: 0;
      background: 0;
      color: #3B334A;
      margin-right: 8px;
    }

    &__rules {
      width: 392px;
      list-style: none;
      margin-top: 48px;
      padding: 48px 64px;
      padding-bottom: 0;
      box-sizing: content-box;
    }

    &__rule {
      position: relative;
      color: $white;
      background-color: #3b334a;
      font-size: 0.8125rem;
      font-weight: 500;
      line-height: 1.25rem;
      letter-spacing: -.2px;
      padding: 15px 50px 15px 25px;
      margin-bottom: 8px;
      border-radius: 15px;
      box-shadow: 0 5px 20px rgba(103, 70, 162, .18);

      &-name {
        font-size: 0.6875rem;
        letter-spacing: -0.17px;
      }

      &-delete {
        background: none;
        cursor: pointer;
        position: absolute;
        right: 0;
        padding: 15px 25px 15px 8px;
        border: none;
      }
    }

    &__toggle {
      align-items: center;
      border: 0;
      width: 50px;
      padding: 4px;
      display: flex;
      flex-shrink: 0;
      margin-right: 12px;
      border-radius: 50px;
      background-color: #FFF;
      box-shadow: 0 5px 20px rgba(103, 70, 162, .18);

      .automation-builder__btn {
        width: 24px;
        height: 24px;
        background-color: $green;
        border-radius: 50%;
        transition: 150ms;

        &--inline {
          margin: 0;
          position: relative;
        }
      }

      &--active .automation-builder__btn {
        margin-left: 18px;
      }
    }

    &__step {
      width: 392px;
      padding: 48px 64px;
      text-align: center;
      position: relative;
      box-sizing: content-box;

      &-type {
        margin: 8px 0;
        display: flex;
        align-items: center;

        .automation-builder__step-help {
          white-space: nowrap;
        }
      }

      &-title {
        margin: 0;
        font-size: 11px;
        color: #7123db;
        font-weight: 700;
        line-height: 12px;
        letter-spacing: -0.17px;
      }

      &-help,
      &-description {
        margin: 0;
        font-size: 13px;
        font-weight: 500;
        line-height: 24px;
        letter-spacing: -0.2px;
      }

      &-description {
        color: #3b334a;
      }

      &-help {
        color: #857a98;
      }
    }

    &__add-rule {
      width: 34px;
      height: 34px;
      color: $gray-dark;
      background-color: $green;
      font-size: 1.75rem;
      line-height: 1;
      border: 0;
      position: absolute;
      left: 243px;
      border-radius: 50%;
      z-index: 2;

      &--not-empty {
        margin: 0;
        width: 46px;
        height: 46px;
        border: 6px solid $white-gray-2;
      }
    }

    &__wrapper {
      width: 100%;
      padding-top: 16px;
      position: relative;
      will-change: transform;
      transform-origin: top left;
    }

    &--dragging {
      cursor: grabbing;
      cursor: -webkit-grabbing;
    }

    .slide-right {
      &.in {
        right: 0;
      }

      &-enter-active,
      &-leave-active {
        transition: right 300ms;
      }

      &-enter,
      &-leave-to {
        right: -380px;
      }
    }
  }
</style>
